import { useCallback, useState } from 'react';
import { SfFunnelMenu } from './SfFunnelMenu';
import { ConfirmDeleteFunnelDialog } from './ConfirmDeleteFunnelDialog';
import { useRemoveDeleteSfFunnelFromCanvas } from './useRemoveDeleteSfFunnelFromCanvas';
import useConfirm from '../../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import ConfirmDestructiveDialog from '../../../../common/dialogs/ConfirmDestructiveDialog';
import {
  removeFunnel,
  selectFunnelMap,
} from '../../../../../reducers/united-canvas/unitedCanvasReducer';
import { useDispatch, useSelector } from 'react-redux';
import DeployDialog from '../deploy/DeployDialog';
import { setIsPaywallDialogOpen } from '../../../../../reducers/canvasPageStateReducer';
import usePaywall from '../../../../dashboard/paywall/usePaywall';

interface ConnectedSfFunnelMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  funnel: Funnel;
  disabled?: boolean;
  onDeleteOrRemove: () => void;
}

export const ConnectedSfFunnelMenu = ({
  anchorEl,
  onClose,
  funnel,
  disabled = false,
  onDeleteOrRemove,
}: ConnectedSfFunnelMenuProps) => {
  const funnelMap = useSelector(selectFunnelMap);

  const { handleDeleteFunnel } = useRemoveDeleteSfFunnelFromCanvas();
  const { openConfirm, onCancel, onConfirm } = useConfirm();
  const { isPaywallActive } = usePaywall();
  const dispatch = useDispatch();

  const [deployOrgId, setDeployOrgId] = useState<string | null>(null);

  const onRemoveFunnel = useCallback(() => {
    openConfirm(
      <ConfirmDestructiveDialog
        open={true}
        onConfirm={() => {
          dispatch(removeFunnel({ funnelId: funnel.id }));
          onConfirm();
          onDeleteOrRemove();
        }}
        onClose={onCancel}
        title={`Remove “${funnel.name}”?`}
        confirmText="Remove"
        cancelText="Cancel"
      >
        Are you sure you want to remove “{funnel.name}“ from the canvas?
      </ConfirmDestructiveDialog>,
    );
  }, [openConfirm, onCancel, funnel.name, funnel.id, dispatch, onConfirm, onDeleteOrRemove]);

  const onDeleteFunnel = useCallback(() => {
    openConfirm(
      <ConfirmDeleteFunnelDialog
        open
        onClose={onCancel}
        onConfirm={async () => {
          await handleDeleteFunnel(funnel.id);
          onConfirm();
          onDeleteOrRemove();
        }}
      />,
    );
  }, [openConfirm, onCancel, handleDeleteFunnel, funnel.id, onConfirm, onDeleteOrRemove]);

  const onDeployClick = () => {
    if (isPaywallActive) {
      dispatch(setIsPaywallDialogOpen(true));
    } else {
      setDeployOrgId(funnel.crmOrgId);
    }
  };

  return (
    <>
      <SfFunnelMenu
        anchorEl={anchorEl}
        onClose={onClose}
        onClick={(action) => {
          switch (action) {
            case 'deploy':
              onDeployClick();
              break;
            case 'remove':
              onRemoveFunnel();
              break;
            case 'delete':
              onDeleteFunnel();
              onClose();
              break;
            default:
              onClose();
              break;
          }
        }}
        disabled={disabled}
      />
      {deployOrgId !== null && (
        <DeployDialog
          open={true}
          setIsOpen={(isOpen) => {
            if (!isOpen) {
              setDeployOrgId(null);
              onClose();
              onClose();
            }
          }}
          initialCrmOrgId={deployOrgId}
          funnels={[funnel]}
          funnelMapId={funnelMap.id}
          initialFunnelId={funnel.id}
          initialStep="step2"
        />
      )}
    </>
  );
};
